// app/providers.tsx
'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, Suspense, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useUser } from '@clerk/nextjs'
import { useNavigationStore } from '@/stores/navigation-store'
import { useUserStore } from '@/stores/user-store'

import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'

export function PostHogProvider({ children }: { children: React.ReactNode }) {
    useEffect(() => {
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
            api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
            person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
            capture_pageview: false, // Disable automatic pageview capture, as we capture manually
            loaded: (posthog) => {
                console.log('🎉 PostHog fully loaded and ready')
                // Check if we have a stored user ID from a previous session
                const storedUserId = localStorage.getItem('posthog_user_id')
                if (storedUserId) {
                    console.log('🔄 Re-identifying user from stored user ID:', storedUserId)
                    posthog.identify(storedUserId)
                }
            }
        })
    }, [])

    return (
        <PHProvider client={posthog}>
            <SuspendedPostHogPageView />
            {children}
        </PHProvider>
    )
}

function PostHogPageView() {
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const posthog = usePostHog()

    // Track pageviews
    useEffect(() => {
        if (pathname && posthog) {
            let url = window.origin + pathname
            if (searchParams.toString()) {
                url = url + '?' + searchParams.toString()
            }

            posthog.capture('$pageview', { $current_url: url })
        }
    }, [pathname, searchParams, posthog])

    return null
}

export function PostHogUserIdentification() {
    const posthog = usePostHog()
    const { user: clerkUser, isLoaded: isClerkLoaded } = useUser()
    const { user: dbUser } = useUserStore()

    // Identify user when we have both Clerk data AND database user data
    useEffect(() => {
        console.log('🔍 PostHogUserIdentification:', {
            posthog: !!posthog,
            isClerkLoaded,
            clerkUserId: clerkUser?.id,
            dbUserId: dbUser?.id,
            dbUserAuthId: dbUser?.authId
        })

        // Wait for both Clerk authentication AND internal user data
        if (posthog && isClerkLoaded && clerkUser?.id && dbUser?.id) {
            try {
                console.log('👤 Full database user object:', dbUser)
                console.log('👤 Identifying user in PostHog with user ID:', {
                    userId: dbUser.id,
                    clerkUserId: clerkUser.id,
                    email: dbUser.email,
                    name: dbUser.name,
                    authId: dbUser.authId
                })

                // Use the database user ID, not the Clerk ID
                console.log('🔄 Calling posthog.identify with user ID')
                posthog.identify(dbUser.id)

                // Then set user properties from the database user
                console.log('🔄 Setting user properties from database')
                posthog.people.set({
                    email: dbUser.email,
                    name: dbUser.name,
                    user_id: dbUser.id,
                    auth_id: dbUser.authId,
                    clerk_user_id: clerkUser.id,
                    organization_id: dbUser.organizationId,
                    app_role_id: dbUser.appRoleId
                })

                // Store the user ID for re-identification on reload
                if (typeof window !== 'undefined') {
                    localStorage.setItem('posthog_user_id', dbUser.id)
                }

                console.log('✅ PostHog identify and people.set called successfully with user ID')

                // Test capture with the identified user
                posthog.capture('user_identified', {
                    user_id: dbUser.id,
                    clerk_user_id: clerkUser.id,
                    email: dbUser.email,
                    distinct_id: posthog.get_distinct_id?.() || 'unknown'
                })

                console.log('📊 Current distinct ID after identify:', posthog.get_distinct_id?.())

            } catch (error) {
                console.error('❌ Failed to identify user in PostHog:', error)
            }
        }
    }, [posthog, clerkUser, isClerkLoaded, dbUser])

    return null
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
    return (
        <Suspense fallback={null}>
            <PostHogPageView />
        </Suspense>
    )
}

function GlobalErrorHandlers() {
    const [mounted, setMounted] = useState(false)
    const posthog = usePostHog()
    const { user } = useUser()
    const currentWorkspaceId = useNavigationStore(state => state.currentWorkspaceId)

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        if (!mounted) return

        const handleError = (event: ErrorEvent) => {
            if (posthog) {
                try {
                    posthog.capture('$exception', {
                        $exception_type: 'UnhandledError',
                        $exception_message: event.message,
                        $exception_stack: event.error?.stack,
                        filename: event.filename,
                        lineno: event.lineno,
                        colno: event.colno,
                        url: window.location.href,
                        workspace_id: currentWorkspaceId,
                        user_id: user?.id,
                        environment: process.env.NODE_ENV,
                    })
                } catch (posthogError) {
                    console.error('Failed to capture unhandled error to PostHog:', posthogError)
                }
            }
        }

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            if (posthog) {
                try {
                    posthog.capture('$exception', {
                        $exception_type: 'UnhandledPromiseRejection',
                        $exception_message: event.reason instanceof Error ? event.reason.message : String(event.reason),
                        $exception_stack: event.reason instanceof Error ? event.reason.stack : undefined,
                        url: window.location.href,
                        workspace_id: currentWorkspaceId,
                        user_id: user?.id,
                        environment: process.env.NODE_ENV,
                    })
                } catch (posthogError) {
                    console.error('Failed to capture unhandled promise rejection to PostHog:', posthogError)
                }
            }
        }

        window.addEventListener('error', handleError)
        window.addEventListener('unhandledrejection', handleUnhandledRejection)

        return () => {
            window.removeEventListener('error', handleError)
            window.removeEventListener('unhandledrejection', handleUnhandledRejection)
        }
    }, [posthog, user, currentWorkspaceId, mounted])

    // Don't render anything until mounted to prevent SSR issues
    if (!mounted) return null

    return null
}
